// THE COMPOSITION ROOT: config → gate → shell → routed page.
//
// ---------------------------------------------------------------------------
// THE PAGE IS BEHIND THE GATE, NOT BESIDE IT
// ---------------------------------------------------------------------------
// Recipe §5: "the demo PAGE itself is behind the middleware (even a thin launcher) — an
// open page that merely delegates one call is NOT compliant." Nothing of the demo renders
// until `productAccess(state)` yields a session.
//
// ---------------------------------------------------------------------------
// NO FLASH OF CONTENT — THE SHELL ITSELF IS BEHIND THE GATE
// ---------------------------------------------------------------------------
// Infra's §5 confirmation for a static SPA requires that the app UI not paint before the
// gate decides. `<Shell>` — header, nav, whoami — renders ONLY for an authenticated
// session; `checking` and `anonymous` render `<PreGate>`, which is unbranded and carries
// no app content, so a visitor about to be bounced to Connect never sees chrome appear
// and vanish.
//
// The other two conditions hold by construction: nothing sensitive is in the bundle (it
// is a launcher — hostnames and an app key), and every API call is enforced server-side.
//
// ---------------------------------------------------------------------------
// AUTHENTICATION IS NOT AUTHORISATION, AND HIDING A LINK IS NOT ACCESS CONTROL
// ---------------------------------------------------------------------------
// What a session may DO comes from the fail-closed resolver in `./auth/capabilities.ts`.
// The nav shows only pages the session can open, AND the router re-checks the requested
// id — so a bookmark, a typed hash or a stale URL after a role change is refused rather
// than served.
//
// Every write is re-checked INSIDE its mutator, not only via a control's `disabled`
// attribute. A disabled button is an affordance; `force: true`, devtools or a stale
// render all route around it. The mutators below are the gate.

import * as React from 'react';
import { AnshinShield } from '@anshinus/anna-sdk-react';
import { loadDemoConfig, type DemoConfig, type DemoEnv } from './demo-config';
import { prepSession } from './prep-session';
import { productAccess, type GateState } from './auth/gate';
import { useGate, type GateHandle } from './auth/use-gate';
import type { ResolvedSession } from './auth/dto';
import { canWrite, resolveCapabilities, type Capabilities } from './auth/capabilities';
import {
  BRAND_PROFILE,
  BUILD_BRAND,
  agentName,
  applyBrandAttribute,
  headerWordmark,
  productName,
  withAgent,
} from './shell/brand';
import { hashForPage, navGroups, pageIdFromHash, resolvePage, type PageDef } from './shell/pages';
import {
  DEFAULT_SUMMARY,
  PAGE_COMPONENTS,
  type DemoState,
  type PageProps,
} from './shell/demo-pages';

export function App() {
  React.useEffect(() => applyBrandAttribute(), []);

  const [config, configError] = React.useMemo((): [DemoConfig | null, Error | null] => {
    try {
      return [loadDemoConfig(), null];
    } catch (err) {
      return [null, err instanceof Error ? err : new Error(String(err))];
    }
  }, []);

  if (configError || !config) {
    return (
      <main
        className="demo"
        data-testid="demo-root"
        data-gate-state="config-error"
        data-authenticated="false"
        data-tier="none"
      >
        <section className="panel panel--error" data-testid="config-error">
          <h2>Configuration refused</h2>
          <p className="mono" data-testid="config-error-message">
            {configError?.message ?? 'unknown configuration error'}
          </p>
          <p>
            The demo did not start. It refuses a configuration it cannot honour rather than
            substituting a working value — a UAT tool that silently corrects its own
            misconfiguration tests a deployment nobody made.
          </p>
        </section>
      </main>
    );
  }

  return <Gated config={config} />;
}

function Gated({ config }: { config: DemoConfig }) {
  const gate = useGate({
    connect: config.connect,
    connectBaseUrlOrNull: config.connectBaseUrlOrNull,
    heldByConfig: config.heldByConfig,
    bridgeApiUrl: config.bridgeApiUrl,
  });

  // THE CHOKEPOINT. Nothing below this line runs without a real, scoped user.
  const session = productAccess(gate.state);
  if (!session) return <Denied config={config} gate={gate} />;

  return <Authenticated config={config} session={session} gateState={gate.state} />;
}

function Authenticated({
  config,
  session,
  gateState,
}: {
  config: DemoConfig;
  session: ResolvedSession;
  gateState: GateState;
}) {
  const caps = resolveCapabilities(session);

  const [state, setState] = React.useState<DemoState>({
    env: config.env,
    summary: DEFAULT_SUMMARY,
    prep: null,
    prepPending: false,
    lastLaunch: null,
  });

  // Read inside `runPrep` so it does not have to be rebuilt on every keystroke — and so
  // it cannot capture a stale summary, which is the bug the ref exists to avoid.
  const stateRef = React.useRef(state);
  stateRef.current = state;

  // ---- guarded mutators: the capability is re-checked HERE, at the write ----
  const actions = React.useMemo<PageProps['actions']>(
    () => ({
      setEnv: (env: DemoEnv) => {
        if (!canWrite(caps, 'writeSettings')) return;
        setState((s) => ({ ...s, env }));
      },
      setSummary: (summary: string) => {
        if (!canWrite(caps, 'writeSettings')) return;
        setState((s) => ({ ...s, summary }));
      },
      resetSandbox: () => {
        if (!canWrite(caps, 'resetSandbox')) return;
        setState({
          env: config.env,
          summary: DEFAULT_SUMMARY,
          prep: null,
          prepPending: false,
          lastLaunch: null,
        });
      },
      onLaunch: (lastLaunch) => setState((s) => ({ ...s, lastLaunch })),
      runPrep: () => {
        setState((s) => ({ ...s, prepPending: true, prep: null }));
        void (async () => {
          const result = await prepSession({
            baseUrl: config.baseUrl,
            // THE USER'S OWN TENANT. Not a configured value — there is no configured value.
            tenantId: session.tenantId,
            src: config.callerSrc,
            richContext: {
              summary: stateRef.current.summary,
              entity_refs: [`demo:${config.imageTag}`],
            },
          });
          setState((s) => ({ ...s, prep: result, prepPending: false }));
        })();
      },
    }),
    [caps, config.baseUrl, config.callerSrc, config.env, config.imageTag, session.tenantId],
  );

  // ---- routing ----
  const [hash, setHash] = React.useState(() =>
    typeof window === 'undefined' ? '' : window.location.hash,
  );
  React.useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const resolution = resolvePage(caps, pageIdFromHash(hash));
  const page = resolution.kind === 'ok' ? resolution.page : resolution.fallback;
  const Body = PAGE_COMPONENTS[page.id];

  return (
    <Shell
      gateState={gateState}
      session={session}
      config={config}
      caps={caps}
      page={page}
      onNavigate={(id) => {
        const h = hashForPage(id);
        window.location.hash = h;
        setHash(h);
      }}
    >
      {resolution.kind === 'denied' && (
        <div className="panel panel--warn" data-testid="page-denied">
          <p>
            <strong>{resolution.requested.label}</strong> is not available to the{' '}
            <code>{caps.tier}</code> tier — it requires{' '}
            <code>{resolution.requested.requires}</code>. Showing {resolution.fallback.label}{' '}
            instead.
          </p>
          <p>
            Refused by the router, not merely hidden from the menu. Hiding a link is not
            access control.
          </p>
        </div>
      )}
      {resolution.kind === 'unknown' && (
        <div className="panel panel--warn" data-testid="page-unknown">
          <p>
            No page <code>{resolution.requestedId}</code>. Showing {resolution.fallback.label}.
          </p>
        </div>
      )}
      {Body ? (
        <Body config={config} session={session} caps={caps} state={state} actions={actions} />
      ) : (
        <p className="mono" data-testid="page-missing-component">
          no component registered for page &quot;{page.id}&quot;
        </p>
      )}
    </Shell>
  );
}

/**
 * Everything that is not an authenticated user: checking, held, anonymous, error.
 *
 * One component for all four because they share the critical property — the demo is not
 * mounted — and keeping them together makes it obvious none has a path to the product.
 */
function Denied({ config, gate }: { config: DemoConfig; gate: GateHandle }) {
  const { state } = gate;

  // NO APP UI, NO BRANDED SHELL, for the two states that are about to navigate away or
  // are still deciding. This is the §5 no-flash condition.
  if (state.kind === 'checking' || state.kind === 'anonymous') {
    return <PreGate state={state} bridging={gate.bridging} bridgeError={gate.bridgeError} />;
  }

  return (
    <main
      className="demo"
      data-testid="demo-root"
      data-gate-state={state.kind}
      data-authenticated="false"
      data-tier="none"
    >
      <h1>{productName()}</h1>

      {gate.bridgeError && (
        <section className="panel panel--error" data-testid="gate-bridge-error">
          <h2>Sign-in did not complete</h2>
          <p className="mono">{gate.bridgeError}</p>
          <p>
            The exchange code is single-use, IP-bound and valid for 30 seconds. Reloading
            will not reuse it — sign in again.
          </p>
        </section>
      )}

      {state.kind === 'held' && (
        <section className="panel panel--warn" data-testid="gate-held">
          <h2>Awaiting login configuration</h2>
          <p>
            This deployment is in <strong>held mode</strong>:{' '}
            {state.reason === 'connect-not-configured'
              ? 'no Connect base URL has been configured yet.'
              : 'login has been held by configuration.'}{' '}
            The real Connect login flips in as a values change, not a rebuild.
          </p>
          <p>
            <strong>Held is not a bypass.</strong> The demo is not mounted, no tenant is
            assumed, and nothing is called on the {agentName()} product API — not as this user
            and not as a service identity. There is no service credential in this image.
          </p>
        </section>
      )}

      {state.kind === 'error' && (
        <section className="panel panel--error" data-testid="gate-error">
          <h2>Cannot establish a session</h2>
          <p className="mono">{state.detail}</p>
          <p>
            Reported rather than worked around. The demo fails closed: it does not fall
            back to an unscoped call.
          </p>
          <button type="button" className="action" data-testid="gate-retry" onClick={gate.refresh}>
            Retry
          </button>
        </section>
      )}

      <p className="note">
        Connect: <code>{config.connectBaseUrlOrNull ?? '(not configured)'}</code>
      </p>
    </main>
  );
}

/**
 * The pre-gate screen: `checking` and `anonymous`.
 *
 * UNBRANDED AND CONTENT-FREE on purpose. No shield, no nav, no SDK facts — nothing a
 * visitor could mistake for the app having loaded, and nothing that could appear and then
 * vanish when the redirect fires.
 */
function PreGate({
  state,
  bridging,
  bridgeError,
}: {
  state: GateState;
  bridging: boolean;
  bridgeError: string | null;
}) {
  return (
    <main
      className="pregate"
      data-testid="demo-root"
      data-gate-state={state.kind}
      data-authenticated="false"
      data-tier="none"
    >
      <p className="pregate__status" data-testid="pregate-status">
        {bridging
          ? 'Completing sign-in…'
          : state.kind === 'anonymous'
            ? 'Redirecting to Anshin Connect…'
            : 'Checking your session…'}
      </p>
      {bridgeError && (
        <p className="mono" data-testid="gate-bridge-error">
          {bridgeError}
        </p>
      )}
    </main>
  );
}

function Shell({
  children,
  gateState,
  session,
  config,
  caps,
  page,
  onNavigate,
}: {
  children: React.ReactNode;
  gateState: GateState;
  session: ResolvedSession;
  config: DemoConfig;
  caps: Capabilities;
  page: PageDef;
  onNavigate: (id: string) => void;
}) {
  const groups = navGroups(caps);
  return (
    <div
      className="demo"
      data-testid="demo-root"
      data-gate-state={gateState.kind}
      data-authenticated="true"
      data-tier={caps.tier}
      data-brand={BUILD_BRAND}
      data-page={page.id}
    >
      <header className="head">
        <div className="head__title">
          <AnshinShield size={28} />
          {/* ANSHIN / Anna SDK Demo — every part from the brand profile. The agent
              name is brand data for this SDK, so it is never a literal here. */}
          <span className="head__wordmark" data-testid="wordmark">
            {headerWordmark()}
          </span>
          <span className="head__org" data-testid="brand-org">
            {BRAND_PROFILE.orgName}
          </span>
        </div>
        <dl className="facts facts--inline" data-testid="whoami">
          <Fact label="Signed in" value={session.email} testId="session-email" />
          <Fact label="Tenant" value={session.tenantId} testId="session-tenant" />
          <Fact
            label="Roles"
            value={session.roles.length ? session.roles.join(', ') : '(none)'}
            testId="session-roles"
          />
          <Fact
            label="Platform admin"
            value={session.isPlatformAdmin ? 'yes' : 'no'}
            testId="session-platform-admin"
          />
          <Fact label="Tier" value={caps.tier} testId="session-tier" />
          <Fact label="Image" value={config.imageTag} testId="header-image-tag" />
        </dl>
      </header>

      <div className="layout">
        <nav className="nav" aria-label="Demo sections" data-testid="nav">
          {groups.map((g) => (
            <div key={g.group} className="nav__group" data-testid={`nav-group-${slug(g.group)}`}>
              <h2 className="nav__heading">{g.group}</h2>
              <ul>
                {g.pages.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className={`nav__link${p.id === page.id ? ' nav__link--on' : ''}`}
                      aria-current={p.id === page.id ? 'page' : undefined}
                      data-testid={`nav-${p.id}`}
                      onClick={() => onNavigate(p.id)}
                    >
                      {p.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <main className="content">
          <h1 data-testid="page-title">{page.label}</h1>
          <p className="note" data-testid="page-blurb">
            {withAgent(page.blurb)}
          </p>
          {children}
        </main>
      </div>
    </div>
  );
}

function Fact({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <>
      <dt>{label}</dt>
      <dd className="mono" data-testid={testId}>
        {value}
      </dd>
    </>
  );
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}
